/**
 * Validation côté client pour ATOM Group
 * Sécurité et expérience utilisateur optimisées
 */

// Patterns de validation suisse
export const SWISS_PATTERNS = {
  phone: /^(\+41|0041|0)[1-9][0-9]{8,9}$/,
  avs: /^756\.[0-9]{4}\.[0-9]{4}\.[0-9]{2}$/,
  postalCode: /^[1-9][0-9]{3}$/
};

// Domaines d'email autorisés
export const ALLOWED_EMAIL_DOMAINS = [
  'gmail.com', 'outlook.com', 'hotmail.com', 'yahoo.com',
  'bluewin.ch', 'sunrise.ch', 'gmail.ch', 'yahoo.ch',
  'protonmail.ch', 'protonmail.com', 'tutanota.com'
];

// Mots interdits pour la sécurité
const FORBIDDEN_WORDS = [
  'script', 'javascript', 'alert', 'onload', 'onclick', 'onerror',
  'eval', 'expression', 'vbscript', 'jscript', 'behaviour',
  'bitcoin', 'crypto', 'lottery', 'winner', 'congratulation'
];

/**
 * Validation des noms (nom, prénom)
 */
export const validateName = (name, fieldName = 'nom') => {
  if (!name || name.trim().length < 2) {
    return `${fieldName} doit contenir au moins 2 caractères`;
  }
  
  if (name.length > 50) {
    return `${fieldName} ne peut pas dépasser 50 caractères`;
  }
  
  // Caractères autorisés: lettres, espaces, tirets, apostrophes
  const namePattern = /^[a-zA-ZÀ-ÿ\s\-']+$/;
  if (!namePattern.test(name)) {
    return `${fieldName} ne peut contenir que des lettres, espaces, tirets et apostrophes`;
  }
  
  return null;
};

/**
 * Validation de l'email
 */
export const validateEmail = (email) => {
  if (!email) {
    return "Email requis";
  }
  
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailPattern.test(email)) {
    return "Format d'email invalide";
  }
  
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) {
    return "Domaine email invalide";
  }
  
  // Accepter tous les domaines email valides
  
  return null;
};

/**
 * Validation du téléphone international
 */
export const validateInternationalPhone = (phone) => {
  if (!phone) {
    return "Numéro de téléphone requis";
  }
  
  // Nettoyer le numéro
  const cleanPhone = phone.replace(/[\s\-()\.]/g, '');
  
  // Pattern international plus flexible
  // Accepte: +33123456789, 0033123456789, +41791234567, etc.
  const internationalPattern = /^(\+|00)?[1-9]\d{6,14}$/;
  
  if (!internationalPattern.test(cleanPhone)) {
    return "Format de téléphone invalide. Utilisez un format international (ex: +33 1 23 45 67 89)";
  }
  
  // Vérifier la longueur (entre 7 et 15 chiffres selon norme ITU-T E.164)
  const digitsOnly = cleanPhone.replace(/^\+|^00/, '');
  if (digitsOnly.length < 7 || digitsOnly.length > 15) {
    return "Le numéro doit contenir entre 7 et 15 chiffres";
  }
  
  return null;
};

/**
 * Validation du numéro AVS suisse
 */
export const validateSwissAVS = (avs) => {
  if (!avs) {
    return "Numéro AVS requis";
  }
  
  const cleanAVS = avs.replace(/\s/g, '');
  
  if (!SWISS_PATTERNS.avs.test(cleanAVS)) {
    return "Format AVS invalide: 756.XXXX.XXXX.XX";
  }
  
  return null;
};

/**
 * Validation du code postal suisse
 */
export const validateSwissPostalCode = (postalCode) => {
  if (!postalCode) {
    return "Code postal requis";
  }
  
  if (!SWISS_PATTERNS.postalCode.test(postalCode)) {
    return "Code postal suisse invalide (1000-9999)";
  }
  
  return null;
};

/**
 * Validation du contenu texte (sécurité)
 */
export const validateTextContent = (text, fieldName = 'champ', maxLength = 2000) => {
  if (!text) return null;
  
  if (text.length > maxLength) {
    return `${fieldName} ne peut pas dépasser ${maxLength} caractères`;
  }
  
  // Vérifier les mots interdits
  const lowerText = text.toLowerCase();
  for (const forbiddenWord of FORBIDDEN_WORDS) {
    if (lowerText.includes(forbiddenWord)) {
      return `Contenu non autorisé détecté dans ${fieldName}`;
    }
  }
  
  // Vérifier les tentatives d'injection
  if (text.includes('<script') || text.includes('javascript:') || text.includes('data:')) {
    return `Contenu suspect détecté dans ${fieldName}`;
  }
  
  return null;
};

/**
 * Formatage automatique du téléphone international
 */
export const formatInternationalPhone = (phone) => {
  if (!phone) return '';
  
  // Supprimer tous les caractères non numériques sauf le +
  let cleaned = phone.replace(/[^\d+]/g, '');
  
  // Si commence par 00, remplacer par +
  if (cleaned.startsWith('00')) {
    cleaned = '+' + cleaned.slice(2);
  }
  
  // Si ne commence pas par +, l'ajouter si le numéro semble international
  if (!cleaned.startsWith('+') && cleaned.length > 10) {
    cleaned = '+' + cleaned;
  }
  
  // Formatage léger avec espaces pour lisibilité
  if (cleaned.startsWith('+')) {
    const withoutPlus = cleaned.slice(1);
    if (withoutPlus.length > 3) {
      const countryCode = withoutPlus.slice(0, 2);
      const rest = withoutPlus.slice(2);
      
      // Formater par groupes de 2-3 chiffres
      const formatted = rest.replace(/(\d{2,3})(?=\d)/g, '$1 ');
      return `+${countryCode} ${formatted}`.trim();
    }
  }
  
  return cleaned;
};

/**
 * Formatage automatique de l'AVS
 */
export const formatSwissAVS = (avs) => {
  if (!avs) return '';
  
  // Supprimer tous les caractères non numériques
  const numbers = avs.replace(/\D/g, '');
  
  // Ajouter les points automatiquement
  if (numbers.length >= 3) {
    let formatted = '756';
    if (numbers.length > 3) {
      formatted += '.' + numbers.slice(3, 7);
    }
    if (numbers.length > 7) {
      formatted += '.' + numbers.slice(7, 11);
    }
    if (numbers.length > 11) {
      formatted += '.' + numbers.slice(11, 13);
    }
    return formatted;
  }
  
  return numbers;
};

/**
 * Validation complète du formulaire de contact
 */
export const validateContactForm = (formData) => {
  const errors = {};
  
  // Validation des champs obligatoires
  const nomError = validateName(formData.nom, 'Nom');
  if (nomError) errors.nom = nomError;
  
  const prenomError = validateName(formData.prenom, 'Prénom');
  if (prenomError) errors.prenom = prenomError;
  
  const emailError = validateEmail(formData.email);
  if (emailError) errors.email = emailError;
  
  if (!formData.theme) {
    errors.theme = "Veuillez sélectionner un thème";
  }
  
  // Validation du message optionnel
  if (formData.message) {
    const messageError = validateTextContent(formData.message, 'message', 1000);
    if (messageError) errors.message = messageError;
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Validation complète du formulaire Avoirs LPP
 */
export const validateAvoirsLPPForm = (formData) => {
  const errors = {};
  
  // Noms
  const firstNameError = validateName(formData.firstName, 'Prénom');
  if (firstNameError) errors.firstName = firstNameError;
  
  const lastNameError = validateName(formData.lastName, 'Nom');
  if (lastNameError) errors.lastName = lastNameError;
  
  // Email
  const emailError = validateEmail(formData.email);
  if (emailError) errors.email = emailError;
  
  // Adresse
  if (!formData.address || formData.address.trim().length < 5) {
    errors.address = "Adresse complète requise";
  }
  
  if (!formData.city || formData.city.trim().length < 2) {
    errors.city = "Ville requise";
  }
  
  // Validations spécifiques suisses
  const postalError = validateSwissPostalCode(formData.postalCode);
  if (postalError) errors.postalCode = postalError;
  
  const avsError = validateSwissAVS(formData.avsNumber);
  if (avsError) errors.avsNumber = avsError;
  
  // Date de naissance
  if (!formData.dateOfBirth) {
    errors.dateOfBirth = "Date de naissance requise";
  } else {
    const birthDate = new Date(formData.dateOfBirth);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    
    if (age < 18 || age > 100) {
      errors.dateOfBirth = "Âge doit être entre 18 et 100 ans";
    }
  }
  
  // Champs obligatoires
  if (!formData.gender) {
    errors.gender = "Genre requis";
  }
  
  if (!formData.formerEmployers || formData.formerEmployers.trim().length < 3) {
    errors.formerEmployers = "Employeurs précédents requis";
  }
  
  if (!formData.motif || formData.motif.trim().length < 3) {
    errors.motif = "Motif de la recherche requis";
  }
  
  // Acceptation des conditions
  if (!formData.signature) {
    errors.signature = "Signature requise";
  }
  
  if (!formData.acceptTerms) {
    errors.acceptTerms = "Acceptation des conditions requise";
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Nettoyage sécurisé du texte
 */
export const sanitizeInput = (input) => {
  if (!input) return '';
  
  return input
    .trim()
    .replace(/[<>]/g, '') // Supprimer < et >
    .replace(/javascript:/gi, '') // Supprimer javascript:
    .replace(/data:/gi, '') // Supprimer data:
    .slice(0, 2000); // Limiter la longueur
};